import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  cropImage,
  deleteImage,
  updateImage,
  type Cast,
  type ChapterImage,
  type CropRegion,
} from "../api";
import ChapterFigure from "../components/ChapterFigure";
import MutationError from "../components/MutationError";
import SheetHeader from "../components/SheetHeader";
import { useT } from "../i18n";
import Cropper from "./Cropper";

/**
 * The pieces of a chapter's pictures in the studio's edit mode (ADR-0038,
 * ADR-0051): the notices about its cast, the gap a picture goes into, a
 * placed picture with what can be done to it, and the pictures that lost
 * their paragraph.
 *
 * A picture is a row beside the chapter, anchored to what a paragraph *says*,
 * so adding one neither makes a revision nor invalidates the audio that has
 * already been recorded.
 */

/** A write that waits its turn behind the editor's others (ADR-0051). */
export type Run = <T>(write: () => Promise<T>) => Promise<T>;

/**
 * CastNotices says what stands between this chapter and a drawn picture.
 *
 * Reading the book for its cast is a prerequisite (ADR-0039): without a
 * roster nobody in a description can be matched to a face. Faces are not — a
 * picture that names somebody without one says so in the sheet and is
 * refused on its own (ADR-0044), so the rest of the book is not held up.
 */
export function CastNotices({ cast, bookID }: { cast?: Cast; bookID: string }) {
  const t = useT();
  if (!cast) return null;
  const faceless = cast.total - cast.with_portraits;
  return (
    <>
      {!cast.ready && (
        <section
          data-testid="cast-not-ready"
          className="mb-4 rounded-card border border-amber-300 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/30"
        >
          <h3 className="text-sm font-medium text-amber-800 dark:text-amber-300">
            {t.portraits.castTitle}
          </h3>
          <p className="mt-1 text-xs leading-5 text-amber-800/80 dark:text-amber-300/80">
            {t.portraits.castWhy}
          </p>
          <p data-testid="cast-missing" className="mt-2 text-sm text-amber-900 dark:text-amber-200">
            {t.portraits.castNotExtracted}
          </p>
          <Link
            to={`/studio/books/${bookID}/characters`}
            data-testid="cast-link"
            className="mt-3 inline-block rounded-control bg-sage px-3 py-1.5 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep"
          >
            {t.portraits.goToCast}
          </Link>
        </section>
      )}

      {/* Not a wall: who still has no face, so an author can draw them
          before writing the scene that needs them. */}
      {cast.ready && faceless > 0 && (
        <p data-testid="cast-faceless" className="mb-2 text-xs text-muted">
          {cast.missing.length === faceless
            ? t.portraits.castMissing(cast.missing)
            : t.portraits.castMissingCount(faceless, cast.total)}{" "}
          {t.portraits.castLazy}{" "}
          <Link
            to={`/studio/books/${bookID}/characters`}
            data-testid="cast-faceless-link"
            className="text-sage-deep underline underline-offset-2"
          >
            {t.portraits.goToCast}
          </Link>
        </p>
      )}

      {cast.ready && cast.stale > 0 && (
        <p data-testid="cast-stale" className="mb-4 text-xs text-muted">
          {t.portraits.castStale(cast.stale)}
        </p>
      )}
    </>
  );
}

/**
 * UnplacedPictures are the ones whose paragraph is gone, with the words they
 * used to follow, so the author can put each back.
 */
export function UnplacedPictures({
  chapterID,
  images,
  onMove,
  onReplace,
  onChanged,
  run,
}: {
  chapterID: string;
  images: ChapterImage[];
  onMove: (image: ChapterImage) => void;
  onReplace: (image: ChapterImage) => void;
  onChanged: () => Promise<unknown>;
  run: Run;
}) {
  const t = useT();
  if (images.length === 0) return null;
  return (
    <section
      data-testid="unplaced-images"
      className="mt-8 rounded-card border border-amber-300 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/30"
    >
      <h3 className="text-sm font-medium text-amber-800 dark:text-amber-300">
        {t.images.unplaced(images.length)}
      </h3>
      <p className="mt-1 text-xs leading-5 text-amber-800/80 dark:text-amber-300/80">
        {t.images.unplacedWhy}
      </p>
      {images.map((image) => (
        <div key={image.id} className="mt-4">
          {image.anchor_text && (
            <p
              dir="auto"
              data-testid="unplaced-anchor"
              className="mb-1 line-clamp-2 text-xs text-amber-800/80 dark:text-amber-300/80"
            >
              {t.images.followed(image.anchor_text)}
            </p>
          )}
          <Placed
            chapterID={chapterID}
            image={image}
            onMove={onMove}
            onReplace={onReplace}
            onChanged={onChanged}
            run={run}
            // Unplaced pictures are shown only to someone who can place them.
            canEdit
          />
        </div>
      ))}
    </section>
  );
}

/**
 * Inserter is the gap between two paragraphs, made tappable.
 *
 * A hairline with a small target on it rather than a button on every
 * paragraph: at rest it is almost invisible, so the chapter still reads as a
 * chapter, and it is 40px of touch target for a thumb. While a picture is
 * being moved every one of them says so instead.
 */
export function Inserter({
  label,
  onClick,
  moving,
  disabled = false,
}: {
  label: string;
  onClick: () => void;
  moving: boolean;
  /** Nobody has read the book for its cast, so a tap here could produce nothing. */
  disabled?: boolean;
}) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={moving ? t.images.moveHere : label}
      data-testid={moving ? "image-move-target" : "image-insert"}
      className="group flex w-full items-center gap-2 py-2 disabled:opacity-30"
    >
      <span
        className={`h-px flex-1 transition-colors ${
          moving ? "bg-sage" : "bg-line group-hover:bg-sage"
        }`}
        aria-hidden="true"
      />
      <span
        className={`grid shrink-0 place-items-center rounded-full border text-xs transition-colors ${
          moving
            ? "border-sage bg-sage-soft px-2.5 py-0.5 text-sage-deep"
            : "size-6 border-line text-muted group-hover:border-sage group-hover:text-sage-deep"
        }`}
      >
        {moving ? t.images.moveHere : <span aria-hidden="true">+</span>}
      </span>
      <span
        className={`h-px flex-1 transition-colors ${
          moving ? "bg-sage" : "bg-line group-hover:bg-sage"
        }`}
        aria-hidden="true"
      />
    </button>
  );
}

/**
 * Placed is a picture in the chapter, with the things that can be done to it:
 * replace it with another in the same place (ADR-0053), crop it, caption it,
 * move it, remove it. Pills rather than text links, so each is a thumb's
 * target on a phone.
 *
 * The caption and the screen-reader description are edited here rather than in
 * the sheet that made it: a caption is written once the picture is in place
 * and the author can see it against the words above it.
 */
/** One action on a placed picture: a pill, 32px tall, for a thumb. */
const pill = "inline-flex min-h-8 items-center rounded-full border px-3 transition-colors";

export function Placed({
  chapterID,
  image,
  onMove,
  onReplace,
  onChanged,
  run,
  canEdit,
}: {
  chapterID: string;
  image: ChapterImage;
  onMove: (image: ChapterImage) => void;
  onReplace: (image: ChapterImage) => void;
  onChanged: () => Promise<unknown>;
  run: Run;
  // canEdit false is a linked book: the picture is the source's and every write
  // to it is refused (ADR-0098 §1). The picture itself stays — leaving the
  // controls out is not leaving the picture out. Required, so a call site that
  // forgets the lock does not get the controls by default.
  canEdit: boolean;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [cropping, setCropping] = useState(false);
  const [caption, setCaption] = useState(image.caption ?? "");
  const [alt, setAlt] = useState(image.alt ?? "");

  const save = useMutation({
    mutationFn: () => run(() => updateImage(image.id, { caption, alt })),
    onSuccess: async () => {
      setOpen(false);
      await onChanged();
    },
  });

  const remove = useMutation({
    mutationFn: () => run(() => deleteImage(image.id)),
    onSuccess: () => onChanged(),
  });

  // A crop is a new picture in the same place, with the same words and cast
  // (ADR-0048): cut it, then point this one at it.
  const crop = useMutation({
    mutationFn: (region: CropRegion) =>
      run(async () => {
        const cut = await cropImage(chapterID, image.object_key, region);
        return updateImage(image.id, { object_key: cut.object_key });
      }),
    onSuccess: async () => {
      setCropping(false);
      await onChanged();
    },
  });

  const changed = caption !== (image.caption ?? "") || alt !== (image.alt ?? "");

  if (!canEdit) return <ChapterFigure image={image} />;

  return (
    <ChapterFigure image={image}>
      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
        <button
          type="button"
          data-testid="image-replace"
          onClick={() => onReplace(image)}
          className={`${pill} border-sage font-medium text-sage-deep hover:bg-sage-soft`}
        >
          {t.images.replace}
        </button>
        <button
          type="button"
          data-testid="image-crop-placed"
          onClick={() => setCropping(true)}
          className={`${pill} border-line text-ink-soft hover:bg-sunk`}
        >
          {t.crop.crop}
        </button>
        <button
          type="button"
          data-testid="image-edit"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className={`${pill} border-line text-ink-soft hover:bg-sunk`}
        >
          {t.images.caption}
        </button>
        <button
          type="button"
          data-testid="image-move"
          onClick={() => onMove(image)}
          className={`${pill} border-line text-ink-soft hover:bg-sunk`}
        >
          {t.images.move}
        </button>
        <button
          type="button"
          data-testid="image-remove"
          disabled={remove.isPending}
          onClick={() => {
            if (window.confirm(t.images.removeConfirm)) remove.mutate();
          }}
          className={`${pill} border-line text-red-600 hover:bg-red-50 disabled:opacity-40 dark:text-red-400 dark:hover:bg-red-950/40`}
        >
          {t.images.remove}
        </button>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[0.7rem]">
        <span dir="auto" className="text-muted">
          {image.drawn_from?.length
            ? t.images.drawnFrom(image.drawn_from)
            : image.source === "generated"
              ? t.images.generatedNote
              : t.images.uploadedNote}
        </span>
        {/* A picture with no description is invisible to a screen reader, and
            it is the one thing here nobody notices is missing. */}
        {!image.alt && (
          <span data-testid="image-alt-missing" className="text-amber-700 dark:text-amber-400">
            {t.images.altMissing}
          </span>
        )}
        {/* Readers do not have this picture yet (ADR-0062) — a placement, a
            caption or a crop is a draft until the book is published. Said in
            the same quiet register as the missing description, because the
            edition panel is the authoritative list of what would go out. */}
        {!image.published && (
          <span data-testid="image-not-published" className="text-muted">
            {t.images.notPublished}
          </span>
        )}
      </div>

      {open && (
        <div data-testid="image-editor" className="mt-2 space-y-2 rounded-control bg-sunk p-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-ink-soft">{t.images.caption}</span>
            <input
              dir="auto"
              value={caption}
              maxLength={300}
              data-testid="image-caption-input"
              placeholder={t.images.captionPlaceholder}
              onChange={(e) => setCaption(e.target.value)}
              className="block w-full rounded-control border border-line bg-surface px-3 py-2 text-base"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-ink-soft">{t.images.alt}</span>
            <input
              dir="auto"
              value={alt}
              maxLength={300}
              data-testid="image-alt-input"
              placeholder={t.images.altPlaceholder}
              onChange={(e) => setAlt(e.target.value)}
              className="block w-full rounded-control border border-line bg-surface px-3 py-2 text-base"
            />
          </label>
          <button
            type="button"
            data-testid="image-save"
            disabled={!changed || save.isPending}
            onClick={() => save.mutate()}
            className="rounded-control bg-sage px-3 py-1.5 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
          >
            {t.images.save}
          </button>
          {/* On success the editor closes; without this a refusal leaves it
              open with nothing said, and the author presses Save again. */}
          <MutationError error={save.error} fallback={t.images.saveFailed} />
        </div>
      )}
      {cropping && (
        <div
          role="dialog"
          aria-label={t.crop.title}
          data-testid="crop-sheet"
          className="fixed inset-x-0 bottom-0 z-40 max-h-[95dvh] overflow-y-auto rounded-t-card border-t border-line bg-surface shadow-[0_-8px_24px_-12px_rgb(0_0_0/0.25)]"
        >
          <div className="mx-auto w-full max-w-3xl">
            <SheetHeader
              title={t.crop.title}
              onClose={() => setCropping(false)}
              closeTestID="crop-sheet-close"
            />
            <div className="px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <Cropper
                src={image.url}
                shape={null}
                applying={crop.isPending}
                error={crop.error}
                onApply={(region) => crop.mutate(region)}
                onCancel={() => {
                  crop.reset();
                  setCropping(false);
                }}
              />
            </div>
          </div>
        </div>
      )}
      <MutationError
        className="mt-1"
        testId="image-remove-error"
        error={remove.error}
        fallback={t.images.removeFailed}
      />
    </ChapterFigure>
  );
}
